import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { formatDate, isPlatformServer } from '@angular/common';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { Category } from '../interfaces/category';
import { HttpClient } from '@angular/common/http';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { Post } from '../interfaces/post';
import { CultureService } from '../culture/culture.service';
//import { getBlogCategoriesTree } from '../../../fake-server';

/// One post on the lists, as GET /api/Blog/GetBlogList sends it: newest first, no body.
interface BlogListItem {
    slug: string;
    title: string;
    subtitle: string;
    category: string;
    coverPhoto: string;
    createDate: string;
    /// 'en' or 'bn' (missing on a post from before languages: English).
    language?: string;
    metaDescription?: string;
    /// The post's own address: "/blog/{slug}", "/bn/blog/{slug}", or "/sell/stories/{slug}" for a story.
    path?: string;
}

/// The server render's list, carried in the page so the browser does not ask for it again.
const BLOG_LIST = makeStateKey<BlogListItem[]>('blog-list');

/// One post a search found, as GET /api/Blog/Search sends it: a list row and, where the words are
/// in the body, a few words of it.
interface BlogSearchItem extends BlogListItem {
    excerpt: string;
}

/// The words of a search as the server reads them (BlogSearch.Terms): split on white space, lower case,
/// each cut at 40 characters, no repeats, six at most. The page marks exactly what was looked for.
export function searchTerms(query: string): string[] {
    const words = String(query || '').trim().slice(0, 100).split(/\s+/)
        .map(word => word.toLowerCase().slice(0, 40))
        .filter(word => word.length > 0);
    return words.filter((word, i) => words.indexOf(word) === i).slice(0, 6);
}

/// The names getCategories gives the blog's categories, by slug - PrimeHat's three (buyers, sellers, stories) and the
/// older posts' ones.
const CATEGORY_NAMES: { [slug: string]: string } = {
    buyers: 'For buyers', sellers: 'For sellers', stories: 'Stories',
    review: 'Review', blog: 'Blog', updates: 'Updates',
};

/// The labels of those names, for a page in another language.
const CATEGORY_KEYS: { [slug: string]: string } = {
    buyers: 'blog-for-buyers', sellers: 'blog-for-sellers', stories: 'blog-stories',
    review: 'cat-review', blog: 'blog', updates: 'cat-updates',
};

/// A category as the blog names it: "review" reads "Review" (in the page's language when culture is given). A name it
/// does not know reads as it was typed.
export function blogCategoryName(category: string, culture?: CultureService): string {
    const slug = String(category || '').trim();
    const name = CATEGORY_NAMES[slug.toLowerCase()] || slug;
    const key = CATEGORY_KEYS[slug.toLowerCase()];
    return culture && key ? culture.t(key, name) : name;
}

/// A post's date as the pages print it, "September 18, 2026" (on a Bangla page "18 সেপ্টেম্বর 2026"), taken from the
/// date part alone: the server render and every browser, whatever its time zone, then print the same day.
export function blogDate(value: string, lang?: string): string {
    const day = /^\d{4}-\d{2}-\d{2}/.exec(String(value || ''));
    if (!day) { return ''; }
    return lang === 'bn' ? formatDate(day[0] + 'T00:00:00Z', 'd MMMM y', 'bn', 'UTC')
        : formatDate(day[0] + 'T00:00:00Z', 'MMMM d, y', 'en-US', 'UTC');
}

function toPost(row: BlogListItem, culture?: CultureService): Post {
    const category = (row.category || '').trim();
    return {
        title: row.title,
        image: row.coverPhoto,
        categories: category ? [blogCategoryName(category, culture)] : [],
        date: blogDate(row.createDate, culture && culture.lang),
        slug: row.slug,
        categorySlug: category,
        shortDescrip: row.subtitle,
        path: row.path || '',
        language: row.language === 'bn' ? 'bn' : 'en',
    };
}

function toSearchPost(row: BlogSearchItem, culture?: CultureService): Post {
    return Object.assign(toPost(row, culture), { excerpt: row.excerpt || '' });
}

@Injectable({
    providedIn: 'root'
})
export class BlogService {
    baseUrl:string = environment.apiUrl;

    /// Every post, asked for once per visit and shared by every list that shows them.
    private posts$: Observable<Post[]> = null;

    /// Set by refresh(): the list is answered with "may be kept five minutes", so the next
    /// request goes under an address this browser has not kept.
    private refreshedAt = 0;

    constructor(
        private http: HttpClient,
        private state: TransferState,
        @Inject(PLATFORM_ID) private platformId: any,
        private culture: CultureService,
    ) { }

    getCategories(cat:string = '', query = ''): Observable<Category[]> {

        const top_categories: Category[] = [
            {
              "id": 2,
              "parentId": 1,
              "name": "Review",
              "type": "blog",
              "path": "",
              "image": "mobile-phone",
              "items": 989,
              "parents": [],
              "children": [],
              "slug": "review"
            },
            {
                "id": 12,
                "parentId": 1,
                "name": "Blog",
                "type": "blog",
                "path": "",
                "image": "fridge",
                "items": 428,
                "parents": [],
                "children": [],
                "slug": "blog"
            },
            {
              "id": 12,
              "parentId": 1,
              "name": "Updates",
              "type": "blog",
              "path": "",
              "image": "fridge",
              "items": 428,
              "parents": [],
              "children": [],
              "slug": "updates"
            }
          ];
        return of(top_categories);//getBlogCategoriesTree(parent ? parent.slug : null, depth);
    }

    /// One post. The server render's answer travels in the page (TransferState), so the browser draws the post it was
    /// sent without asking again; it is read once, and a later visit to the post asks the server.
    getBlogDetail(slug:string):Observable<any>
    {
        const key = makeStateKey<any>('blog-post:' + slug);
        const carried = this.state.get<any>(key, null);
        if (carried) {
            this.state.remove(key);
            return of(carried);
        }
        return this.http.get<any>(this.baseUrl+`/api/Blog/GetBlog?slug=${encodeURIComponent(slug)}`).pipe(
            tap(post => {
                if (isPlatformServer(this.platformId) && post) {
                    this.state.set(key, post);
                }
            }));
    }

    /// The posts for the lists - the home page, the blog page, a post page's sidebar and related
    /// posts - newest first, one category's when `cat` names it. They come from the table
    /// (Blog/GetBlogList) and used to be typed in here, so a new post reached no list without a
    /// deploy. It must cost the pages nothing the typed-in array did not, so: one request per visit,
    /// shared by every list; on a page drawn on the server that request is the server's, and its
    /// answer travels in the page (TransferState), so the browser shows the same posts at once and
    /// asks for nothing. It never fails - the blog page's resolver would cancel the navigation - and
    /// a failed request gives no posts, is not kept, and is tried again by the next list to ask.
    /// With words in `query` it is the blog search's list instead (see search), best match first. `lang`: the posts
    /// in that language only - the English lists never show a Bangla post, /bn/blog only the Bangla ones.
    getBlogList(cat:string = '', query = '', lang = 'en'):Observable<Post[]>
    {
        const terms = searchTerms(query);
        const posts$ = terms.length > 0 ? this.search(terms.join(' ')) : this.allPosts();
        return posts$.pipe(
            map(posts => posts.filter(p => (p.language || 'en') === lang)),
            map(posts => cat && cat !== '' ? posts.filter(p => p.categorySlug == cat) : posts));
    }

    /// The posts whose title, summary, tags, category or body hold every word (Blog/Search), best first,
    /// each with a few words of its body where the words are. Everyone gets the same answer, so like the
    /// list it is asked for on the server and carried in the page: the browser shows the results at once
    /// and asks for nothing. It never fails: a request that does gives no posts.
    private search(query: string): Observable<Post[]>
    {
        const key = makeStateKey<BlogSearchItem[]>('blog-search:' + query);
        const carried = this.refreshedAt ? null : this.state.get<BlogSearchItem[]>(key, null);

        const rows$: Observable<BlogSearchItem[]> = carried
            ? of(carried)
            : this.http.get<BlogSearchItem[]>(this.baseUrl + '/api/Blog/Search', { params: { query } }).pipe(
                tap(rows => {
                    if (isPlatformServer(this.platformId)) {
                        this.state.set(key, rows || []);
                    }
                }),
                catchError(() => of([] as BlogSearchItem[])));

        return rows$.pipe(map(rows => (rows || []).map(row => toSearchPost(row, this.culture))));
    }

    /// Forgets the posts held for this visit, so the next list to ask reads the table as it is
    /// now. The owner's blog editor calls it after a save or a delete; the server drops its own
    /// copy at the same time.
    refresh(): void
    {
        this.posts$ = null;
        this.state.remove(BLOG_LIST);
        this.refreshedAt = Date.now();
    }

    private allPosts(): Observable<Post[]>
    {
        if (!this.posts$)
        {
            const carried = this.state.get<BlogListItem[]>(BLOG_LIST, null);

            const rows$: Observable<BlogListItem[]> = carried
                ? of(carried)
                : this.http.get<BlogListItem[]>(this.baseUrl + '/api/Blog/GetBlogList'
                    + (this.refreshedAt ? '?v=' + this.refreshedAt : '')).pipe(
                    tap(rows => {
                        if (isPlatformServer(this.platformId)) {
                            this.state.set(BLOG_LIST, rows || []);
                        }
                    }),
                    catchError(() => {
                        this.posts$ = null;
                        return of([] as BlogListItem[]);
                    }));

            this.posts$ = rows$.pipe(
                map(rows => (rows || []).map(row => toPost(row, this.culture))),
                shareReplay(1));
        }

        return this.posts$;
    }
}
